import { createApp } from 'vue'
import { createPinia } from 'pinia'
import axios from 'axios'
import { BASE_URL } from './config/api.js'
import versionService from './services/versionService.js'
import { isMustChangePassword } from './utils/mustChangePassword.js'
import { installBackgroundScrollLock } from './utils/backgroundScrollLock.js'

// CSS crítico para o boot (shell + overlay). Demais folhas após mount.
import './assets/css/main.css'
import './assets/css/vue-components.css'
import './assets/css/mobile-responsive.css'
import './assets/css/design-system-fluid.css'
import './assets/css/status-chip.css'
import './assets/css/portal-compact-table.css'
import './assets/css/portal-checkbox.css'
import './assets/css/portal-pagination.css'
import './assets/css/portal-dark.css'

/** Inicia download do App.vue em paralelo com o restante do boot. */
const appModulePromise = import('./App.vue')

function loadDeferredPortalStyles() {
  const load = () => {
    Promise.all([
      import('./assets/css/agenda-schedules-list-historico.css'),
      import('./assets/css/expeditions-list.css'),
      import('./assets/css/rejeicoes-list.css'),
      import('./assets/css/grupo-oscar-list.css'),
      import('./assets/css/grupo-oscar-agendamentos-list.css'),
      import('./assets/css/garoto-list.css'),
      import('./assets/css/carga-descarga-list.css'),
      import('./assets/css/admin-users-list.css'),
      import('./assets/css/propostas-geracao-list.css'),
      import('./assets/css/manage-user-shell.css'),
      import('./assets/css/portal-glass-shell.css'),
      import('./assets/css/booking-workflow-shell.css'),
    ])
      .then(() => import('./assets/css/light-surface-ink.css'))
      /* Opaco no escuro: depois do glass/listas deferred, para vencer rgba branco + blur. */
      .then(() => import('./assets/css/portal-dark-opaque.css'))
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(load, { timeout: 2500 })
  } else {
    setTimeout(load, 80)
  }
}

loadDeferredPortalStyles()
installBackgroundScrollLock()

// Chaves de sessionStorage que protegem o reload automático contra loop.
// Mesmo padrão do guard de bfcache (portalBfcacheReload) logo abaixo.
const CHUNK_RELOAD_FLAG = 'portalChunkReload'
const CHUNK_RELOAD_AT = 'portalChunkReloadAt'
const CHUNK_RELOAD_COOLDOWN_MS = 10000

/** Erro típico de chunk removido por um deploy (import dinâmico falhou). */
function isDynamicImportError(error) {
  const msg = String(error?.message || error || '').toLowerCase()
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('dynamically imported module') ||
    (msg.includes('expected a javascript') && msg.includes('module script'))
  )
}

/**
 * Recarrega a página UMA vez para recuperar de chunk JS obsoleto (hash antigo
 * removido pelo deploy e servido como index.html pelo catch-all do IIS).
 * Protegido contra loop: a flag de sessão barra um 2º reload até um mount
 * bem-sucedido limpá-la, e o cooldown por tempo (que sobrevive ao mount) barra
 * o caso "mount ok → falha imediata → reload". Foi o loop em mobile que levou o
 * commit 59670a1a a desligar o listener em produção; aqui religamos com guarda.
 */
function reloadOnceForStaleChunk(reason) {
  if (typeof window === 'undefined') return false
  try {
    const now = Date.now()
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      console.warn(
        `[boot] Chunk obsoleto persiste após reload (${reason}) — não recarrega de novo (anti-loop)`
      )
      return false
    }
    const lastAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT) || 0)
    if (lastAt && now - lastAt < CHUNK_RELOAD_COOLDOWN_MS) {
      console.warn(
        `[boot] Reload por chunk recente demais (${reason}) — abortando (anti-loop)`
      )
      return false
    }
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
    sessionStorage.setItem(CHUNK_RELOAD_AT, String(now))
  } catch (_) {
    // sessionStorage indisponível — segue para o reload mesmo assim
  }
  console.warn(`[boot] Chunk desatualizado (${reason}) — recarregando página`)
  window.location.reload()
  return true
}

// Chunk obsoleto após novo deploy (aba antiga referencia hash já removido) ou,
// em dev, após restart do Vite. `vite:preloadError` é o mecanismo oficial do
// Vite para o caso — ativo em produção E em dev, com proteção contra loop.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', event => {
    // CSS/asset preload ("Unable to preload CSS…") não é chunk JS morto:
    // recarregar a página inteira aqui causa loop ao abrir rotas lazy
    // (ex.: Administração > Clientes). Só recarrega em erro de import
    // dinâmico. Se o guard bloquear ou o erro não for de chunk JS, NÃO
    // dá preventDefault — o import rejeita e o onError do lazy trata.
    if (!isDynamicImportError(event.payload || event)) return
    if (reloadOnceForStaleChunk('vite:preloadError')) event.preventDefault()
  })
}

function removeBootSplashElement() {
  document.getElementById('app-boot-splash')?.remove()
}

if (typeof window !== 'undefined') {
  window.__portalMainLoaded = true

  window.addEventListener('pageshow', event => {
    if (!event.persisted) return
    try {
      const reloaded = sessionStorage.getItem('portalBfcacheReload')
      if (reloaded) return
      sessionStorage.setItem('portalBfcacheReload', '1')
      console.warn('[boot] Restaurado do cache do navegador — recarregando')
      window.location.reload()
    } catch (_) {
      window.location.reload()
    }
  })
}

// Configurar axios defaults usando configuração centralizada
axios.defaults.baseURL = BASE_URL

// Interceptor para corrigir URL duplicada /api/api/* (evita 404)
axios.interceptors.request.use(
  config => {
    const base = config.baseURL || ''
    const url = config.url || ''
    // Se baseURL termina com /api e url começa com /api, remover duplicata
    if (
      base &&
      url &&
      (base.endsWith('/api') || base.endsWith('/api/')) &&
      url.startsWith('/api')
    ) {
      config.url = url.replace(/^\/api\/?/, '/') || '/'
    }
    const token = localStorage.getItem('token')
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    console.error('Erro na requisição axios:', error)
    return Promise.reject(error)
  }
)

// Interceptor para logar erros de resposta do axios
axios.interceptors.response.use(
  response => response,
  error => {
    const isAborted =
      error?.message === 'Request aborted' || error?.code === 'ERR_CANCELED'
    if (!isAborted) {
      console.error(
        `[axios] ${error?.response?.status ?? 'network'} ${error.config?.url}`,
        error?.response?.data ?? error.message
      )
    }
    return Promise.reject(error)
  }
)

// SISTEMA SIMPLES - APENAS VERIFICAÇÃO BÁSICA DE TOKEN

// Renderizar fallback de erro quando Vue falha ao montar (evita tela em branco)
function showMountErrorFallback(message, error) {
  console.error('[main.js] Fallback de erro:', message, error)
  removeBootSplashElement()
  const appEl = document.getElementById('app')
  if (!appEl) return
  appEl.innerHTML = `
    <div style="padding: 2rem; max-width: 500px; margin: 2rem auto; font-family: 'Arial', sans-serif; text-align: center; background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.1);">
      <h2 style="color: #333; margin: 0 0 1rem;">Erro ao carregar o sistema</h2>
      <p style="color: #666; margin: 0 0 1.5rem;">${message}</p>
      <button onclick="localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href='/login.html';" 
        style="padding: 0.6rem 1.5rem; background: #0d6efd; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 1rem;">
        Fazer logout e tentar novamente
      </button>
    </div>
  `
}

// Montar app assim que o token existir (sem delay artificial pós-login)
function mountAppWhenReady() {
  const token = localStorage.getItem('token')
  const userData = localStorage.getItem('user')

  if (!token || !userData || userData === 'null' || userData === '{}') {
    try {
      sessionStorage.removeItem('portalSessionStartedAt')
    } catch (_) {
      /* ignore */
    }
    const currentPath = window.location.pathname
    if (currentPath === '/login.html' || currentPath.includes('login.html'))
      return
    window.location.href = '/login.html'
  } else {
    try {
      if (!sessionStorage.getItem('portalSessionStartedAt')) {
        sessionStorage.setItem('portalSessionStartedAt', String(Date.now()))
      }
    } catch (_) {
      /* ignore */
    }

    let user
    try {
      user = JSON.parse(userData)
      // Normalizar level_access (pode vir como string "1" do backend)
      if (
        user &&
        typeof user.level_access !== 'number' &&
        user.level_access != null
      ) {
        user.level_access = Number(user.level_access)
      }
      // Garantir estrutura mínima para evitar erros em usuários específicos
      if (!user || typeof user !== 'object') {
        throw new Error('Dados do usuário inválidos')
      }
      if (!user.cli_access) {
        user.cli_access = {}
      } else if (typeof user.cli_access === 'string') {
        try {
          user.cli_access = JSON.parse(user.cli_access) || {}
        } catch (_) {
          user.cli_access = {}
        }
      }
    } catch (parseError) {
      console.error('Erro ao processar dados do usuário:', parseError)
      window.location.href = '/login.html'
      return
    }

    // Flag local pode estar stale; login.html revalida com GET /users/profile/me
    if (isMustChangePassword(user.must_change_password)) {
      window.location.href = '/login.html'
      return
    }

    // Verificar página e montar app
    const currentPath = window.location.pathname

    if (Number(user.level_access) === 9) {
      if (currentPath.includes('schedule-verification.html')) {
        // Manter em verificação
      } else {
        // Pode acessar ambas
      }
    } else if (currentPath.includes('schedule-verification.html')) {
      console.log(
        'Usuário normal na página de verificação - redirecionando para dashboard'
      )
      window.location.href = '/'
      return
    }

    appModulePromise
      .then(async ({ default: App }) => {
        try {
          const app = createApp(App)
          app.use(createPinia())
          const { vModalEsc } = await import('./directives/modalEsc.js')
          app.directive('modal-esc', vModalEsc)
          app.config.globalProperties.$http = axios
          app.config.globalProperties.$showConfirm = async (
            msg,
            title = 'Confirmação'
          ) => {
            const { useSystemDialogStore } =
              await import('./stores/systemDialog.js')
            return useSystemDialogStore().showConfirm(msg, title)
          }
          app.config.globalProperties.$showAlert = async (
            msg,
            title = 'Aviso'
          ) => {
            const { useSystemDialogStore } =
              await import('./stores/systemDialog.js')
            return useSystemDialogStore().showAlert(msg, title)
          }
          app.config.errorHandler = (err, instance, info) => {
            console.error('[Vue Error]', info, err)
            // Rede de segurança final: se um import dinâmico falhou e escapou
            // das camadas anteriores (vite:preloadError e onError do lazy),
            // tenta o mesmo reload protegido contra loop antes de só logar.
            if (
              isDynamicImportError(err) &&
              reloadOnceForStaleChunk('vue:errorHandler')
            ) {
              return
            }
            try {
              if (instance?.setLoading) instance.setLoading(false)
            } catch (_) {
              /* ignore */
            }
          }
          app.mount('#app')
          requestAnimationFrame(() => removeBootSplashElement())
          window.__portalVueMounted = true
          if (window.__bootSplashWatchdog) {
            clearTimeout(window.__bootSplashWatchdog)
          }
          try {
            sessionStorage.removeItem('portalBfcacheReload')
            // Mount OK: libera o próximo reload por chunk. O cooldown por tempo
            // (portalChunkReloadAt) é mantido de propósito, para barrar um loop
            // "mount ok → falha imediata → reload".
            sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
          } catch (_) {
            /* ignore */
          }
          versionService.startVersionCheck(5)
        } catch (mountError) {
          console.error('Erro ao montar aplicação Vue:', mountError)
          showMountErrorFallback(
            'Não foi possível carregar a interface. Isso pode ocorrer devido a dados de sessão incompatíveis ou problema no navegador.',
            mountError
          )
        }
      })
      .catch(loadError => {
        console.error('Erro ao carregar módulo App.vue:', loadError)
        showMountErrorFallback(
          'Não foi possível carregar os recursos da interface. Verifique a conexão e tente novamente.',
          loadError
        )
      })
  }
}

mountAppWhenReady()
